/**
 * MODULE: domain/pages/frontend/utils/pagesBlockDragTarget.ts
 * PURPOSE: Resolve nested draggable blocks (list items, quote lines) for drag handles
 */

import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

export type DraggableBlockTarget = {
  node: ProseMirrorNode;
  /** Document position immediately before the draggable node. */
  pos: number;
};

const TOP_LEVEL_CONTAINER_TYPES = new Set([
  'bulletList',
  'orderedList',
  'blockquote',
  'horizontalRule',
  'pagesImageBlock',
  'pagesFileBlock',
  'pagesPageLinkBlock',
  'pagesCrmDealPreviewBlock',
  'pagesCrmContactPreviewBlock',
  'pagesCrmCompanyPreviewBlock',
  'pagesToggleBlock',
  'pagesCalloutBlock',
  'pagesTodoBlock',
  'pagesAiBlock',
]);

const NESTED_LEAF_TYPES = new Set(['paragraph', 'heading', 'codeBlock']);

/** Pick the innermost Notion-style draggable unit at a document position. */
export function resolveDraggableBlockAtPos(
  doc: ProseMirrorNode,
  rawPos: number
): DraggableBlockTarget | null {
  const clamped = Math.max(0, Math.min(rawPos, doc.content.size));
  const $pos = doc.resolve(clamped);

  for (let depth = $pos.depth; depth >= 1; depth -= 1) {
    const node = $pos.node(depth);
    const name = node.type.name;
    const posBefore = $pos.before(depth);

    if (name === 'listItem') {
      return { node, pos: posBefore };
    }

    if (NESTED_LEAF_TYPES.has(name)) {
      const parentName = depth > 1 ? $pos.node(depth - 1).type.name : '';
      if (parentName === 'listItem') {
        continue;
      }
      return { node, pos: posBefore };
    }

    if (TOP_LEVEL_CONTAINER_TYPES.has(name) && depth === 1) {
      return { node, pos: posBefore };
    }
  }

  return null;
}

/** True when the target is nested below the document root (list item or quote line). */
export function isNestedDraggableTarget(
  doc: ProseMirrorNode,
  target: DraggableBlockTarget
): boolean {
  const $pos = doc.resolve(target.pos + 1);
  if (target.node.type.name === 'listItem') {
    return true;
  }
  return NESTED_LEAF_TYPES.has(target.node.type.name) && $pos.depth > 1;
}
